/**
 * CategoryEnabledCounter - Displays enabled/total count for a category section (WP-CAT-11)
 *
 * Shows "X of Y enabled" with optional progress indicator styling.
 */
import { cn } from '@/utils/cn';

export interface CategoryEnabledCounterProps {
  /** Number of enabled categories */
  enabled: number;
  /** Total number of categories */
  total: number;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Counter display showing enabled count out of total.
 *
 * @example
 * ```tsx
 * <CategoryEnabledCounter enabled={12} total={15} />
 * // Renders: "12 of 15 enabled"
 * ```
 */
export default function CategoryEnabledCounter({
  enabled,
  total,
  size = 'sm',
  className,
}: CategoryEnabledCounterProps) {
  const sizeStyles = {
    sm: 'text-xs',
    md: 'text-sm',
  };

  return (
    <span
      className={cn(
        'text-gray-500',
        sizeStyles[size],
        className
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {enabled} of {total} enabled
    </span>
  );
}
